<template>
  <Multiselect
    v-model="model"
    :options="options"
    :placeholder="placeholder"
    :searchable="searchable"
    :multiple="multiple"
    :track-by="trackBy"
    :label="label"
    :clear-on-select="clearOnSelect"
    :hide-selected="hideSelected"
    :allow-empty="allowEmpty"
    :close-on-select="closeOnSelect"
    :show-labels="showLabels"
    :loading="loading"
    :class="$style.selector"
  />
</template>

<script setup lang="ts">
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

const model = defineModel<
  string | number | object | unknown[] | null | undefined
>()

type OptionValueType = string | number | boolean | object

// ref: https://vue-multiselect.js.org/#sub-props
withDefaults(
  defineProps<{
    options: OptionValueType[]
    placeholder?: string
    searchable?: boolean

    // Equivalent to the multiple attribute on a <select> input.
    multiple?: boolean

    // Used to compare objects. Only use if options are objects.
    trackBy?: string

    //	Label from option Object, that will be visible in the dropdown.
    label?: string

    // Clear the search input after select(). Use only when multiple is true.
    clearOnSelect?: boolean

    // Hide already selected options
    hideSelected?: boolean

    // Allows to remove all selected values. Otherwise one must be left selected.
    allowEmpty?: boolean

    // the dropdown stays open after selecting an option
    closeOnSelect?: boolean

    // the highlighted option doesn’t have a label on it
    showLabels?: boolean

    // Show/hide the loading spinner.
    loading?: boolean
  }>(),
  {
    placeholder: 'Select option',
    searchable: true,
    multiple: true,
    trackBy: '',
    label: '',
    clearOnSelect: false,
    hideSelected: true,
    allowEmpty: false,
    closeOnSelect: false,
    showLabels: true,
    loading: false,
  }
)
</script>

<!-- see 'node_modules/vue-multiselect/dist/vue-multiselect.css' and override the styles -->
<style lang="scss" module>
.selector {
  :global .multiselect {
    box-shadow: $shadow-0;

    &__placeholder {
      margin-bottom: 9px;
    }

    &__select {
      height: 51px;
      padding: 4px 8px 10px;
    }

    &__tags {
      padding: 10px 40px 6px 16px;
    }

    &__tag {
      position: relative;
      display: inline-block;
      padding: 4px 26px 4px 10px;
      border-radius: 5px;
      margin-right: 10px;
      color: $color-text-and-object-high-emphasis-inverse;
      line-height: 1;
      background: $color-label-blue;
      margin-bottom: 4px;
      white-space: nowrap;
      overflow: hidden;
      max-width: 100%;
      text-overflow: ellipsis;
      font-size: 15px;
    }

    &__option--highlight {
      background: $color-label-blue;
      outline: none;
      color: $color-text-and-object-high-emphasis-inverse;
    }

    &__option--highlight::after {
      content: attr(data-select);
      background: $color-label-blue;
      color: $color-text-and-object-high-emphasis-inverse;
    }
  }
}
</style>
